/**
 * Production-Safe Logger
 * 
 * Provides environment-aware logging that:
 * - Shows detailed logs in development
 * - Suppresses debug/info logs in production
 * - Maintains error/warn logs for production debugging
 * - Integrates with error tracking services
 * 
 * Usage:
 *   import { logger } from '@/utils/logger';
 *   logger.debug('Debug info', data);
 *   logger.info('Info message', data);
 *   logger.warn('Warning', data);
 *   logger.error('Error occurred', error);
 */

const isDev = (import.meta as any).env.DEV;
const isProduction = (import.meta as any).env.PROD;

// Error tracking integration (configure when ready)
// import * as Sentry from '@sentry/browser';

interface LoggerContext {
  [key: string]: any;
}

class Logger {
  /**
   * Debug level - development only
   * Use for detailed debugging information
   */
  debug(message: string, ...args: any[]): void {
    if (isDev) {
      console.debug(`[DEBUG] ${message}`, ...args);
    }
  }

  /**
   * Info level - development only
   * Use for general information
   */
  info(message: string, ...args: any[]): void {
    if (isDev) {
      console.info(`[INFO] ${message}`, ...args);
    }
  }

  /**
   * Warning level - always logged
   * Use for recoverable issues
   */
  warn(message: string, ...args: any[]): void {
    console.warn(`[WARN] ${message}`, ...args);
    
    // Optional: Send to error tracking in production
    if (isProduction) {
      // Sentry.captureMessage(message, 'warning');
    }
  }

  /**
   * Error level - always logged
   * Use for errors and exceptions
   */
  error(message: string, error?: Error | any, context?: LoggerContext): void {
    console.error(`[ERROR] ${message}`, error, context);
    
    // Send to error tracking service in production
    if (isProduction && error) {
      // Sentry.captureException(error, {
      //   contexts: { custom: context },
      //   tags: { message }
      // });
    }
  }

  /**
   * Performance logging - development only
   * Use for performance measurements
   */
  perf(label: string, duration?: number): void {
    if (isDev) {
      console.log(`[PERF] ${label}${duration ? `: ${duration}ms` : ''}`);
    }
  }

  /**
   * Network logging - development only
   * Use for API requests/responses
   */
  network(method: string, url: string, status?: number, duration?: number): void {
    if (isDev) {
      const statusEmoji = status && status >= 200 && status < 300 ? '✅' : '❌';
      console.log(`[NET] ${statusEmoji} ${method} ${url}`, { status, duration });
    }
  }

  /**
   * Firebase operation logging - development only
   * Use for Firestore/Auth operations
   */
  firebase(operation: string, details?: any): void {
    if (isDev) {
      console.log(`[🔥 FIREBASE] ${operation}`, details);
    }
  }

  /**
   * Group logging - development only
   * Use for grouped console output
   */
  group(label: string, fn: () => void): void {
    if (isDev) {
      console.group(label);
      fn();
      console.groupEnd();
    }
  }

  /**
   * Table logging - development only
   * Use for tabular data
   */
  table(data: any): void {
    if (isDev) {
      console.table(data);
    }
  }

  /**
   * Assert logging - development only
   * Use for conditional logging
   */
  assert(condition: boolean, message: string): void {
    if (isDev && !condition) {
      console.error(`[ASSERT] ${message}`);
    }
  }

  /**
   * Time measurement
   * Measures execution time of a function
   */
  async measure<T>(label: string, fn: () => T | Promise<T>): Promise<T> {
    if (!isDev) {
      return await fn();
    }

    const start = performance.now();
    try {
      const result = await fn();
      const duration = performance.now() - start;
      this.perf(label, duration);
      return result;
    } catch (error) {
      const duration = performance.now() - start;
      this.error(`${label} failed after ${duration}ms`, error);
      throw error;
    }
  }
}

// Export singleton instance
export const logger = new Logger();

// Named exports for convenience
export const { debug, info, warn, error, perf, network, firebase, group, table, assert, measure } = logger;

// Export default for default imports
export default logger;

// Production logging helper
export const logProduction = (message: string, data?: any) => {
  if (isProduction) {
    // Could send to analytics/monitoring service
    console.log(`[PROD] ${message}`, data);
  }
};

// Development-only logging helper
export const logDev = (message: string, ...args: any[]) => {
  if (isDev) {
    console.log(`[DEV] ${message}`, ...args);
  }
};
